import { useEffect, useLayoutEffect, useRef } from 'react';
import { useRouter } from 'next/router';

let activeWait: (() => Promise<void>) | null = null;

/**
 * Returns the sequence number of the overlay entry that is current in the
 * browser history, or 0 if the current entry is a regular page entry.
 */
const getCurrentSequence = () =>
  (window.history.state as { overlaySequence?: number } | null)
    ?.overlaySequence ?? 0;

const initialSequence =
  typeof window !== 'undefined' ? getCurrentSequence() : 0;
let pushSequence = initialSequence;
let hasCleanedStaleEntries = false;
let openOverlaysCount = 0;

const BACK_TIMEOUT_MS = 1000;

/**
 * Goes one step back in history and calls onComplete when the browser has
 * actually moved. history.back() is asynchronous, so we have to wait for
 * the popstate event before relying on the new current entry. If popstate
 * never comes, onComplete is still called after a timeout with isTimedOut set,
 * so callers never hang and never leave the router guard on.
 */
const backOnce = (onComplete: (isTimedOut: boolean) => void) => {
  let fallbackTimer = 0;

  const handlePopComplete = () => {
    window.clearTimeout(fallbackTimer);
    window.removeEventListener('popstate', handlePopComplete);
    onComplete(false);
  };

  fallbackTimer = window.setTimeout(() => {
    window.removeEventListener('popstate', handlePopComplete);
    onComplete(true);
  }, BACK_TIMEOUT_MS);

  window.addEventListener('popstate', handlePopComplete);
  window.history.back();
};

/**
 * Lets code outside of the hook (e.g. a payment flow that reloads the page)
 * close the open overlay and wait until its history entry is really removed.
 * Resolves immediately when no overlay is open.
 */
export const waitForActiveHistoryOverlayClose = (): Promise<void> =>
  activeWait ? activeWait() : Promise.resolve();

/**
 * Makes Back (browser or system) close an overlay instead of leaving the page.
 *
 * On open, pushes one extra same-URL history entry marked with a sequence
 * number. Back consumes it and closes the overlay; if the overlay is closed
 * another way, the entry is popped manually. Next.js ignores pops to the same
 * URL while an overlay is open. Sequence numbers let nested overlays close
 * one level at a time.
 *
 * Returns a function that closes the overlay.
 */
export const useHistoryBackClose = (isOpen: boolean, onClose: () => void) => {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const ownsEntryRef = useRef(false);
  const scrollYRef = useRef(0);

  /**
   * Removes entries left by a reload while an overlay was open, so Back does
   * not silently pass through them. initialSequence is read at module load
   * because Next.js later overwrites history.state. Runs once per page load.
   */
  useEffect(() => {
    if (hasCleanedStaleEntries) return;
    hasCleanedStaleEntries = true;

    if (initialSequence === 0) return;

    routerRef.current.beforePopState(() => false);

    let popsLeft = window.history.length;

    const stopStaleCleanup = () => {
      routerRef.current.beforePopState(() => true);
    };

    const popNext = () => {
      backOnce(isTimedOut => {
        popsLeft -= 1;

        if (!isTimedOut && getCurrentSequence() > 0 && popsLeft > 0) {
          popNext();
        } else {
          stopStaleCleanup();
        }
      });
    };

    popNext();
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
    }
  }, [isOpen]);

  /**
   * Main logic: registers the overlay in history while it is open and removes
   * the registration when it closes.
   */
  useEffect(() => {
    if (!isOpen) return undefined;

    pushSequence += 1;
    const ownSequence = pushSequence;
    ownsEntryRef.current = true;

    window.history.pushState(
      { ...window.history.state, overlaySequence: ownSequence },
      ''
    );

    const openAsPath = routerRef.current.asPath;

    openOverlaysCount += 1;
    routerRef.current.beforePopState(state => state.as !== openAsPath);

    let isRoutingReleased = false;

    /**
     * Gives routing back to Next.js. Safe to call several times: each overlay
     * releases only once, and the guard is lifted only when no overlay is open.
     */
    const restoreRouting = () => {
      if (isRoutingReleased) return;
      isRoutingReleased = true;
      openOverlaysCount -= 1;

      if (openOverlaysCount === 0) {
        routerRef.current.beforePopState(() => true);
      }
    };

    /**
     * Reacts to Back / gesture. If our entry is gone (current sequence is lower
     * than ours), the overlay is closed and the scroll position is restored.
     */
    const handlePopState = () => {
      if (getCurrentSequence() < ownSequence) {
        ownsEntryRef.current = false;
        window.scrollTo(0, scrollYRef.current);
        restoreRouting();
        onCloseRef.current();
      }
    };

    /**
     * Removes our history entry ourselves (close button, unmount, or before a
     * page reload) and resolves when the browser has really popped it.
     * Does nothing if the entry is already gone.
     */
    const popEntryAndWait = () => {
      if (!ownsEntryRef.current) return Promise.resolve();
      ownsEntryRef.current = false;

      if (getCurrentSequence() < ownSequence) {
        return Promise.resolve();
      }

      return new Promise<void>(resolve => {
        backOnce(() => {
          window.scrollTo(0, scrollYRef.current);
          restoreRouting();
          resolve();
        });
      });
    };

    window.addEventListener('popstate', handlePopState);
    activeWait = popEntryAndWait;

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (activeWait === popEntryAndWait) {
        activeWait = null;
      }
      popEntryAndWait().then(restoreRouting);
    };
  }, [isOpen]);

  return () => onCloseRef.current();
};
